import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { WallboardViewService } from '../wallboard-view.service';
import { WallboardsService } from '../../wallboards.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { WVCustom, WVMain } from '../wallboard-view.model';
import { ChallengesSetupObj, CurrentChallengeWSObj, ModuleType } from '../../wallboards.model';
import { isChallengeAllowedOnWallboard } from 'src/app/gamification/command-center/models/challenge.model';

@Component({
    selector: 'cv-wallboard-view-challenges',
    templateUrl: './wallboard-view-challenges.component.html',
    styleUrls: ['./wallboard-view-challenges.component.scss']
})
export class WallboardViewChallengesComponent implements OnInit, OnDestroy {
    @Input() firstSlide;

    public wvf: WVMain;
    public wvcf: WVCustom;
    public challengesState: ChallengesSetupObj;
    public activeChallenges: CurrentChallengeWSObj[] = [];

    public isLoading = false;
    public maxRows: number = 0;
    public totalPages: number = 0;
    public currentPage: number = 0;
    public $viewContainer;
    public slideTimer;
    public slideIntervalTime: number = 0;
    public slideError: boolean = false;
    public localization: Map<string, string>;

    private isDestroyed: boolean = false;

    get hasBackdrop(): boolean {
        return this.wvcf?.Slide.HasBackdrop ?? false;
    }

    get moduleKey(): string {
        return this.wvcf?.Slide.WallboardModuleDetails[0].WallboardModuleKey ?? "";
    }

    get backgroundID(): number {
        return this.wvcf?.Slide.BackgroundImageID ?? 0;
    }

    ngOnInit(): void {
        this.wvf = this.service.WVMain;
        this.wvcf = this.service.WVChallenges;

        this.cvLocalizationService.GetSection("Challenges_Messages").then(response => {
            this.localization = response;
        });
    
        this.wvf.RunSlide = this.firstSlide;

        this.$viewContainer = angular.element(document).find('.wallboardViewSlide');
        this.$viewContainer.height('100vh');

        this.isLoading = true;

        let typeState = this.wallboardsService.loadTypeState(ModuleType.Challenges, this.moduleKey, this.wvf.WallboardCreatorID).then(response => {
            if (this.isDestroyed)
                return;

            this.challengesState = response.Challenges_Setup;
        });

        let loginsData = this.service.getChallengesLoginsData(this.wvcf.Slide.WallboardID, this.moduleKey).then(response => {
            if (this.isDestroyed)
                return;

            this.activeChallenges = response.filter(isChallengeAllowedOnWallboard);
        });

        Promise.all([typeState, loginsData]).then(responses => {
            if (!this.firstSlide)
                this.wvf.NextSlideReady = false;

            if (this.activeChallenges.length == 1) {
                let tmpArray = this.activeChallenges[0].Rankings.concat(this.activeChallenges[0].NotRankings);
                this.activeChallenges[0].Rankings = tmpArray;
            }

            this.runSlide();
        }).finally(() => {
            this.isLoading = false;
        });
    }

    ngOnDestroy(): void {
        this.isDestroyed = true;
    }

    constructor(
        private service: WallboardViewService,
        public wallboardsService: WallboardsService,
        private cvLocalizationService: CVLocalizationService,
    ) { }

    runSlide() {
        let calculatedInterval = null;
        // Verifies that the slide is not the first one
        let verifyNextSlide = this.firstSlide == undefined && (this.wvcf.Slide.WallboardModuleDetails[0].WallboardSlideID == this.wvf.Slides[this.wvf.NextSlide].WallboardSlideID);
        if (verifyNextSlide) {
            this.slideTimer = (this.wvf.Slides[this.wvf.NextSlide].SlideInterval);
            this.slideIntervalTime = (this.wvf.Slides[this.wvf.NextSlide].SlideInterval - 1) * 1000;
        } else if (this.wvf.LastSlide != null) {
            this.slideTimer = this.wvf.LastSlide.SlideInterval;
            this.slideIntervalTime = (this.wvf.LastSlide.SlideInterval - 1) * 1000;
        }
        else {
            this.slideTimer = (this.wvf.Slides[this.wvf.CurrentSlide].SlideInterval);
            this.slideIntervalTime = (this.wvf.Slides[this.wvf.CurrentSlide].SlideInterval - 1) * 1000;
        }

        // Setting max number of row displayed per slide
        let totRows = 0;
        if (this.activeChallenges.length == 1 && this.activeChallenges[0].Rankings.length > 2) {
            totRows = this.activeChallenges[0].Rankings.length;
            this.maxRows = 9;
        }
        else if (this.activeChallenges.length > 4) {
            totRows = this.activeChallenges.length;
            this.maxRows = 10;
        }

        if (totRows > this.maxRows) {
            this.totalPages = Math.ceil(totRows / this.maxRows);
            calculatedInterval = ((this.slideTimer / this.totalPages) * 1000) - 1000;

            // Agreed on a minimum of 3 seconds per page
            if (calculatedInterval < 3000) {
                calculatedInterval = 3000;
                this.slideIntervalTime = calculatedInterval * this.totalPages;

                if (this.wvf.Slides[verifyNextSlide ? this.wvf.NextSlide : this.wvf.CurrentSlide])
                    this.wvf.Slides[verifyNextSlide ? this.wvf.NextSlide : this.wvf.CurrentSlide].SlideInterval = this.slideIntervalTime / 1000;

                if (this.wvf.LastSlide)
                    this.wvf.LastSlide.SlideInterval = this.slideIntervalTime / 1000;
            }
        }
        else
            this.currentPage = 0;

        if (this.firstSlide)
            this.wvf.FirstSlideReady = true;
        else
            this.wvf.NextSlideReady = true;

        if (this.wvf.RunSlide) {
            if (calculatedInterval != null && this.totalPages > 0) {
                for (let i = 0; i < this.totalPages; i++) {
                    this.runPagination(calculatedInterval, i);
                }
            }
        }
        else {
            setTimeout(() => {
                this.runSlide();
            }, 1000);
        }
    }

    runPagination(interval, index) {
        setTimeout(() => {
            if (index == 0)
                this.currentPage = 0;
            else
                this.currentPage++;
        }, index * interval);
    }

    setBackground() {
        if (this.hasBackdrop)
            return 'rgba(0, 0, 0, 0.7)';
    }

    getDateString(EndDate) {
        let date1 = new Date();
        date1.setHours(0, 0, 0, 0);
        let date2 = new Date(EndDate);

        if (date1.getTime() == date2.getTime())
            return this.localization?.get("Ends_Today");
        else
            return this.localization?.get("Ends_On") + " " + EndDate;
    }

    getMetric(value) {
        return value == "" || value == "0" || value == "00:00:00" || value == null ? "----" : value;
    }
}
